import { SITE_CONFIG } from "../../app/config";
import { useHead, buildGuideSchema, buildFAQSchema } from "../../seo";
import { guideBySlug } from "./guide-data";

type GuidePageProps = {
  slug: string;
};

export const GuidePage = ({ slug }: GuidePageProps): JSX.Element => {
  const guide = guideBySlug.get(slug);

  if (!guide) {
    return (
      <div className="shell">
        <div className="pseo-page">
          <header className="pseo-header">
            <a href="/guides" className="blog-back">← All Guides</a>
            <h1 className="pseo-title">Guide Not Found</h1>
            <p className="pseo-subtitle">
              This guide doesn't exist.{" "}
              <a href="/guides">Browse all guides →</a>
            </p>
          </header>
        </div>
      </div>
    );
  }

  const guideSchemas = buildGuideSchema({
    title: guide.metaTitle,
    description: guide.metaDescription,
    path: `/guides/${guide.slug}`,
    steps: guide.steps.map((s) => s.heading),
  });

  const faqSchema = guide.faq.length > 0 ? buildFAQSchema(guide.faq) : null;
  const jsonLd = faqSchema ? [...guideSchemas, faqSchema] : guideSchemas;

  useHead({
    title: guide.metaTitle,
    description: guide.metaDescription,
    path: `/guides/${guide.slug}`,
    ogType: "article",
    jsonLd,
  });

  return (
    <div className="shell">
      <div className="pseo-page">
        <header className="pseo-header">
          <a href="/guides" className="blog-back">← All Guides</a>
          <div className="guide-badge">{guide.category}</div>
          <h1 className="pseo-title">{guide.title}</h1>
          <p className="pseo-subtitle">{guide.metaDescription}</p>
        </header>

        <article className="guide-content">
          {/* Introduction */}
          <div className="guide-intro">
            {guide.introduction.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {/* Steps */}
          <div className="guide-steps">
            {guide.steps.map((step, i) => (
              <section key={i} className="guide-step">
                <div className="guide-step-number">Step {i + 1}</div>
                <h2>
                  <span className="guide-step-icon">{step.icon}</span>
                  {step.heading}
                </h2>
                {step.content.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </section>
            ))}
          </div>

          {/* Pro Tips */}
          {guide.proTips.length > 0 && (
            <section className="guide-pro-tips">
              <h2>Pro Tips</h2>
              <ul>
                {guide.proTips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </section>
          )}

          {/* FAQ */}
          {guide.faq.length > 0 && (
            <section className="guide-faq">
              <h2>Frequently Asked Questions</h2>
              {guide.faq.map((item, i) => (
                <div key={i} className="guide-faq-item">
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </div>
              ))}
            </section>
          )}
        </article>

        <footer className="pseo-footer">
          <div className="blog-cta">
            <h3>Ready to Start Your Collection?</h3>
            <p>
              Paste your Spotify playlist and discover where to buy your favorite albums on vinyl
              and CD at the best prices.
            </p>
            <a href="/" className="blog-cta-button">
              Try {SITE_CONFIG.name} Free →
            </a>
          </div>
          <div className="pseo-nav-links">
            <a href="/guides">← All Guides</a>
            <a href="/best">Best Of</a>
            <a href="/glossary">Glossary</a>
            <a href="/">Home</a>
          </div>
        </footer>
      </div>
    </div>
  );
};
